"use client";

import { useEffect, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppImage from "@/components/ui/AppImage";

export default function ProductDetailPage() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.1 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <>
            <Header />
            <main id="main-content" ref={ref}>
                <section className="section-navy" style={{ paddingTop: "140px", paddingBottom: "80px" }}>
                    <div style={{ maxWidth: "1280px", margin: "0 auto" }} className="px-6 lg:px-12 text-center">
                        <div className="flex items-center gap-3 mb-8 reveal justify-center" style={{ paddingTop: "8px" }}>
                            <span className="divider-red" style={{ width: "32px" }} aria-hidden="true" />
                            <span className="type-subheading">PRODUCTS</span>
                        </div>
                        <h1 className="type-display reveal reveal-delay-1 text-white mt-3">Antimony Concentrate (Sulfide)</h1>
                        <p className="reveal reveal-delay-2 text-gray-200 mt-4" style={{ maxWidth: "600px", margin: "16px auto 0" }}>Primary ore-based concentrate (typically 40–60% Sb, subject to final assay).</p>
                    </div>
                </section>

                <section style={{ paddingTop: "60px", paddingBottom: "60px", backgroundColor: "var(--color-navy-mid)" }}>
                    <div style={{ maxWidth: "1000px", margin: "0 auto" }} className="px-6 lg:px-12">
                        <div className="reveal reveal-delay-1 relative h-96 bg-[#1d2233] rounded-lg overflow-hidden shadow-xl">
                            <AppImage
                                src="https://ashevillegemmine.com/cdn/shop/files/PeacockOreedit3.jpg?v=1684344284&width=1800"
                                alt="Antimony Concentrate Product"
                                fill
                                className="object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#1d2233]/40 via-transparent to-transparent" />
                        </div>
                    </div>
                </section>

                <section className="section-navy-mid" style={{ paddingTop: "10px", paddingBottom: "80px" }}>
                    <div style={{ maxWidth: "1280px", margin: "0 auto" }} className="px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 reveal reveal-delay-1">
                            <div className="card p-8">
                                <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "1.5rem", fontWeight: 400, color: "var(--color-white)", marginBottom: "16px" }}>Specification Summary</h2>
                                <p className="type-body">Detailed product specifications are provided in the downloadable spec sheet. Typical parameters and tolerance ranges are agreed with refinery counterparts during contract formation.</p>
                            </div>
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="card p-6">
                                    <h4 style={{ fontFamily: "var(--font-sans)", fontSize: "0.8125rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-red)", marginBottom: "12px" }}>Packaging Standards</h4>
                                    <p className="type-body text-sm">Super-sacks with sealed liners, moisture-controlled and lot-traced.</p>
                                </div>
                                <div className="card p-6">
                                    <h4 style={{ fontFamily: "var(--font-sans)", fontSize: "0.8125rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-red)", marginBottom: "12px" }}>Sampling Protocol</h4>
                                    <p className="type-body text-sm">ISO-aligned sampling and assay procedures for consistent results.</p>
                                </div>
                            </div>
                        </div>

                        <aside className="reveal reveal-delay-2">
                            <div className="card p-6">
                                <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "1.1875rem", fontWeight: 400, color: "var(--color-white)", marginBottom: "16px" }}>Quick Facts</h3>
                                <ul className="mt-3 text-sm space-y-2" style={{ color: "rgba(255, 255, 255, 0.85)" }}>
                                    <li><strong>Typical Sb:</strong> 40–60%</li>
                                    <li><strong>Form:</strong> Concentrate (sulfide)</li>
                                    <li><strong>Packing:</strong> Super-sacks</li>
                                    <li><strong>Origin:</strong> Multiple sources</li>
                                </ul>
                            </div>
                            <div className="mt-6 text-center">
                                <a href="#" className="btn-primary">Download Spec Sheet</a>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
